/**
 * src/scripts/pages/dyad-registration/register.js
 *
 * Dyad supervision registration form: mounts a Stripe Elements card field,
 * confirms a SetupIntent client-side (card data never touches our server),
 * then submits the registration with the resulting payment method id.
 */

/**
 * Mirrors DyadSupervisionService.computeSupervisionFee() server-side: highest
 * priceField (basePrice, or soloPrice when solo:true) among the selected
 * types, plus incrementalPrice for every OTHER selected type. Client-side
 * only for display — the server recomputes and charges from the submitted
 * keys itself, never trusting an amount from here.
 * @param {string[]} selectedKeys
 * @param {Record<string, {basePrice: number, incrementalPrice: number, soloPrice: number}>} types
 * @param {{ solo?: boolean }} [options]
 * @returns {number} dollars
 */
function computeSupervisionFeeDisplay(selectedKeys, types, { solo = false } = {}) {
    const priceField = solo ? 'soloPrice' : 'basePrice';
    const validKeys = selectedKeys.filter((key) => types[key]);
    if (validKeys.length === 0) return 0;

    let highestKey = validKeys[0];
    let highestPrice = Number(types[highestKey][priceField]) || 0;
    for (const key of validKeys) {
        const price = Number(types[key][priceField]) || 0;
        if (price > highestPrice) {
            highestKey = key;
            highestPrice = price;
        }
    }

    const incrementalSum = validKeys
        .filter((key) => key !== highestKey)
        .reduce((sum, key) => sum + (Number(types[key].incrementalPrice) || 0), 0);

    return highestPrice + incrementalSum;
}

function formatDollars(amount) {
    return `$${amount.toFixed(2)}`;
}

document.addEventListener('DOMContentLoaded', () => {
    const form = document.getElementById('dr-form');
    if (!form) return; // session not available — no form rendered

    const errorBox = document.getElementById('dr-error');
    const successBox = document.getElementById('dr-success');
    const submitBtn = document.getElementById('dr-submit');
    const hpTsInput = document.getElementById('dr-hp-ts');

    const setupIntentUrl = form.dataset.setupIntentUrl;
    const registerUrl = form.dataset.registerUrl;
    const stripePublishableKey = form.dataset.stripePublishableKey;
    const recaptchaSiteKey = form.dataset.recaptchaSiteKey;

    const supervisionTypes = window.dyadSupervisionTypes || {};
    const supervisionCheckboxes = Array.from(document.querySelectorAll('.dr-supervision-checkbox'));
    const supervisionErrorBox = document.getElementById('dr-supervision-error');
    const dyadFeeEl = document.getElementById('dr-dyad-fee');
    const feeLabelEl = document.getElementById('dr-fee-label');
    const individualFeeEl = document.getElementById('dr-individual-fee');
    const summaryFallbackLine = document.getElementById('dr-summary-fallback-line');
    const consentDyadFeeEl = document.getElementById('dr-consent-dyad-fee');
    const consentIndividualFeeEl = document.getElementById('dr-consent-individual-fee');
    const consentTextDyad = document.getElementById('dr-consent-text-dyad');
    const consentTextIndividual = document.getElementById('dr-consent-text-individual');
    const fallbackIndividualFeeEl = document.getElementById('dr-fallback-individual-fee');
    const fallbackFieldset = document.getElementById('dr-fallback-fieldset');
    const fallbackIndividualRadio = document.getElementById('dr-fallback-individual');
    const fallbackCancelRadio = document.getElementById('dr-fallback-cancel');
    const sessionTypeRadios = Array.from(document.querySelectorAll('input[name="sessionType"]'));
    const addToCalendarSuccessBox = document.getElementById('dr-add-to-calendar-success');

    function getSelectedSupervisionTypes() {
        return supervisionCheckboxes.filter((cb) => cb.checked).map((cb) => cb.value);
    }

    function getSelectedSessionType() {
        const checked = sessionTypeRadios.find((radio) => radio.checked);
        return checked ? checked.value : 'DYAD';
    }

    function updateFeeDisplay() {
        const selected = getSelectedSupervisionTypes();
        const isIndividual = getSelectedSessionType() === 'INDIVIDUAL';
        if (selected.length === 0) {
            if (dyadFeeEl) dyadFeeEl.textContent = 'Select a supervision type above';
            if (individualFeeEl) individualFeeEl.textContent = '—';
            if (consentDyadFeeEl) consentDyadFeeEl.textContent = '—';
            if (consentIndividualFeeEl) consentIndividualFeeEl.textContent = '—';
            if (fallbackIndividualFeeEl) fallbackIndividualFeeEl.textContent = '—';
            return;
        }
        const dyadFee = formatDollars(computeSupervisionFeeDisplay(selected, supervisionTypes, { solo: false }));
        const individualFee = formatDollars(computeSupervisionFeeDisplay(selected, supervisionTypes, { solo: true }));
        // The main fee line shows whichever price actually applies to the
        // chosen Session Type — basePrice-derived for Dyad, soloPrice-derived
        // for Individual — never both at once.
        if (dyadFeeEl) dyadFeeEl.textContent = isIndividual ? individualFee : `${dyadFee} per participant`;
        if (individualFeeEl) individualFeeEl.textContent = individualFee;
        if (consentDyadFeeEl) consentDyadFeeEl.textContent = dyadFee;
        if (consentIndividualFeeEl) consentIndividualFeeEl.textContent = individualFee;
        if (fallbackIndividualFeeEl) fallbackIndividualFeeEl.textContent = individualFee;
        if (supervisionErrorBox) supervisionErrorBox.classList.add('is-hidden');
    }

    /**
     * Show/hide everything that depends on Dyad vs Individual Session:
     * the "if no second participant..." fallback fieldset (Dyad only — an
     * Individual booking never enters a pairing/fallback state, so its
     * choice is meaningless there), which consent paragraph is shown, the
     * fee-line label, and the fee amounts themselves (soloPrice for
     * Individual, basePrice for Dyad).
     */
    function updateSessionTypeVisibility() {
        const isIndividual = getSelectedSessionType() === 'INDIVIDUAL';

        if (fallbackFieldset) fallbackFieldset.classList.toggle('is-hidden', isIndividual);
        if (fallbackIndividualRadio) fallbackIndividualRadio.required = !isIndividual;
        if (fallbackCancelRadio) fallbackCancelRadio.required = !isIndividual;
        if (isIndividual) {
            // A hidden `required` radio's checked state would otherwise still
            // block/confuse submission — clear it, it's irrelevant once
            // Individual Session is chosen.
            if (fallbackIndividualRadio) fallbackIndividualRadio.checked = false;
            if (fallbackCancelRadio) fallbackCancelRadio.checked = false;
        }

        if (feeLabelEl) feeLabelEl.textContent = isIndividual ? 'Individual session fee:' : 'Dyad fee:';
        if (summaryFallbackLine) summaryFallbackLine.classList.toggle('is-hidden', isIndividual);
        if (consentTextDyad) consentTextDyad.classList.toggle('is-hidden', isIndividual);
        if (consentTextIndividual) consentTextIndividual.classList.toggle('is-hidden', !isIndividual);

        updateFeeDisplay();
    }

    supervisionCheckboxes.forEach((cb) => cb.addEventListener('change', updateFeeDisplay));
    sessionTypeRadios.forEach((radio) => radio.addEventListener('change', updateSessionTypeVisibility));
    updateSessionTypeVisibility();

    function hideNotice(box) {
        if (!box) return;
        box.classList.add('is-hidden');
        box.textContent = '';
    }

    function showError(message) {
        if (!errorBox) return;
        errorBox.textContent = message;
        errorBox.classList.remove('is-hidden');
        hideNotice(successBox);
    }

    function showSuccess(message) {
        if (!successBox) return;
        successBox.textContent = message;
        successBox.classList.remove('is-hidden');
        hideNotice(errorBox);
        form.classList.add('is-hidden');
        if (addToCalendarSuccessBox) addToCalendarSuccessBox.classList.remove('is-hidden');
    }

    function getCsrfToken() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.content : '';
    }

    function injectRecaptchaScript(siteKey) {
        if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        document.head.appendChild(script);
    }

    async function getRecaptchaToken() {
        if (!recaptchaSiteKey) return null;
        if (typeof grecaptcha === 'undefined') {
            try {
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('reCAPTCHA load timeout')), 8000);
                    const poll = setInterval(() => {
                        if (typeof grecaptcha !== 'undefined') {
                            clearInterval(poll);
                            clearTimeout(timeout);
                            resolve();
                        }
                    }, 50);
                });
            } catch {
                return null;
            }
        }
        try {
            return await new Promise((resolve, reject) => {
                grecaptcha.ready(() => {
                    grecaptcha.execute(recaptchaSiteKey, { action: 'dyad_registration' }).then(resolve).catch(reject);
                });
            });
        } catch {
            return null;
        }
    }

    if (recaptchaSiteKey) injectRecaptchaScript(recaptchaSiteKey);

    if (!stripePublishableKey || typeof Stripe === 'undefined') {
        showError('Payment is temporarily unavailable. Please try again later or contact us.');
        submitBtn.disabled = true;
        return;
    }

    const stripe = Stripe(stripePublishableKey);
    const elements = stripe.elements();
    const cardElement = elements.create('card');
    cardElement.mount('#dr-card');

    let submitting = false;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (submitting) return;

        const selectedSupervisionTypes = getSelectedSupervisionTypes();
        if (selectedSupervisionTypes.length === 0) {
            if (supervisionErrorBox) supervisionErrorBox.classList.remove('is-hidden');
            supervisionCheckboxes[0]?.focus();
            return;
        }

        submitting = true;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Processing…';
        hideNotice(errorBox);

        try {
            const setupIntentResponse = await fetch(setupIntentUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            });
            const setupIntentData = await setupIntentResponse.json();
            if (!setupIntentResponse.ok || !setupIntentData.success) {
                throw new Error('This session is no longer available. Please refresh the page.');
            }

            const name = form.elements.name.value;
            const email = form.elements.email.value;

            const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(setupIntentData.clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name, email },
                },
            });

            if (stripeError) {
                throw new Error(stripeError.message || 'Your card could not be verified. Please check your card details.');
            }

            const recaptchaToken = await getRecaptchaToken();
            const sessionType = getSelectedSessionType();
            const fallbackChoiceInput = form.querySelector('input[name="fallbackChoice"]:checked');

            const registerResponse = await fetch(registerUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'x-csrf-token': getCsrfToken(),
                },
                body: JSON.stringify({
                    dyadSessionId: form.dataset.sessionId,
                    name,
                    email,
                    phone: form.elements.phone.value,
                    sessionType,
                    selectedSupervisionTypes,
                    fallbackChoice: fallbackChoiceInput ? fallbackChoiceInput.value : null,
                    authorizationAccepted: form.elements.authorizationAccepted.checked,
                    paymentMethodId: setupIntent.payment_method,
                    recaptchaToken,
                    _hp_ts: hpTsInput ? hpTsInput.value : undefined,
                }),
            });
            const registerData = await registerResponse.json();

            if (!registerResponse.ok || !registerData.success) {
                throw new Error(registerData.message || 'Registration could not be completed. Please try again.');
            }

            if (registerData.code === 'MATCHED') {
                showSuccess('You\'re matched with a second participant! Your card has not been charged yet — we\'ll email you the exact charge date. To cancel before then, please contact us.');
            } else if (registerData.code === 'MATCHED_INDIVIDUAL') {
                showSuccess('You\'re booked for an individual session! Your card has not been charged yet — we\'ll email you the exact charge date. To cancel before then, please contact us.');
            } else {
                showSuccess('You\'re registered! Your card has been saved but not charged — we\'ll email you as soon as a second participant registers or the deadline passes.');
            }
        } catch (err) {
            showError(err.message || 'Something went wrong. Please try again.');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Register';
            submitting = false;
        }
    });
});
